import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUsers,
  FiMessageSquare,
  FiGlobe,
  FiLock,
  FiAward,
  FiShield,
  FiArrowLeft,
  FiBookOpen,
  FiSettings,
  FiMessageCircle,
  FiInfo,
  FiEye,
  FiUserPlus,
  FiAlertCircle,
  FiMenu,
  FiX,
  FiLogOut,
} from "react-icons/fi";
import communityService from "../../../services/communityService";
import CommunityThreads from "../threads/CommunityThreads";
import CommunityMembers from "../CommunityMembers";
import CommunityRooms from "../CommunityRooms";
import CommunitySettings from "../CommunitySettings";
import CommunityChat from "../CommunityChat";
import ModerationDashboard from "../moderation/ModerationDashboard";
import CommunitySpecificSidebar from "./CommunitySpecificSidebar";
import {
  hasReadOnlyAccess,
  getReadOnlyMessage,
  getJoinActionText,
  canDirectJoin,
} from "../../../utils/communityPermissions";
import "./cdash-main.css";

// Animation variants
const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

const contentVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

const CommunityDashboardNew = ({ communityId: propCommunityId }) => {
  // Accept communityId as prop (from PersistentCommunitiesLayout) or fallback to useParams
  const { communityId: paramCommunityId } = useParams();
  const communityId = propCommunityId || paramCommunityId;
  const navigate = useNavigate();

const [community, setCommunity] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("threads");
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);

  const fetchCommunityDashboard = useCallback(async () => {
    try {
      setLoading(true);

      const [detailsResponse, previewResponse] = await Promise.all([
        communityService.getCommunityDetails(communityId),
        communityService.getCommunityPreview(communityId),
      ]);

      if (detailsResponse.success && previewResponse.success) {
        setCommunity(detailsResponse.data.community);
        setDashboardData(previewResponse.data);
      } else {
        throw new Error("Failed to fetch community data");
      }
    } catch (error) {
setError(error.message || "Failed to fetch community dashboard");
      toast.error("Failed to load community dashboard");

      if (error.status === 403) {
        navigate(`/communities/${communityId}/preview`);
      }
    } finally {
      setLoading(false);
    }
  }, [communityId, navigate]);

  // CRITICAL: Only fetch on INITIAL MOUNT, not on every URL change
  // This prevents loading state from unmounting children during navigation
  useEffect(() => {
    // Only fetch if we don't already have data for this community
    if (!community || community._id !== communityId) {
      fetchCommunityDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]); // Only re-fetch when communityId actually changes

  // Add class to parent layout when chat is active for CSS targeting
  useEffect(() => {
    const parentLayout = document.querySelector(
      ".research-rooms-sidebar-layout"
    );
    if (parentLayout) {
      if (activeTab === "chat") {
        parentLayout.classList.add("chat-active");
      } else {
        parentLayout.classList.remove("chat-active");
      }
    }
    return () => {
      const parentLayout = document.querySelector(
        ".research-rooms-sidebar-layout"
      );
      if (parentLayout) {
        parentLayout.classList.remove("chat-active");
      }
    };
  }, [activeTab]);

  const getUserRoleBadge = () => {
    const userMembership =
      dashboardData?.userMembership || community?.userMembership;

    const roleConfig = {
      creator: { icon: FiAward, label: "Creator", className: "creator" },
      moderator: { icon: FiShield, label: "Mod", className: "moderator" },
      member: { icon: FiUsers, label: "Member", className: "member" },
    };

    const config = roleConfig[userMembership?.role];
    if (!config) return null;

    const Icon = config.icon;
    return (
      <span className={`cdash-badge cdash-badge-role ${config.className}`}>
        <Icon size={10} />
        {config.label}
      </span>
    );
  };

  const isModeratorOrCreator = () => {
    const userMembership =
      dashboardData?.userMembership || community?.userMembership;
    return (
      userMembership?.role === "creator" || userMembership?.role === "moderator"
    );
  };

  const userMembership =
    dashboardData?.userMembership || community?.userMembership;
  const isReadOnly = community
    ? hasReadOnlyAccess(community, userMembership)
    : false;
  const readOnlyMessage = community
    ? getReadOnlyMessage(community, userMembership)
    : null;

  const handleJoinCommunity = async () => {
    if (!community) {
      toast.error("Community data not available");
      return;
    }

    try {
      if (canDirectJoin(community, userMembership)) {
        await communityService.directJoinCommunity(communityId);
        toast.success("Successfully joined the community!");
      } else {
        navigate(`/communities/${communityId}/preview`);
      }
      fetchCommunityDashboard();
    } catch (error) {
toast.error(error.message || "Failed to join community");
    }
  };

  // Leave Community Handler
  const handleLeaveCommunity = async () => {
    setLeaveLoading(true);
    try {
      await communityService.leaveCommunity(communityId);
      toast.success("You have left the community");
      setShowLeaveModal(false);
      navigate("/communities");
    } catch (error) {
toast.error(error.message || "Failed to leave community");
    } finally {
      setLeaveLoading(false);
    }
  };

  const canLeave = userMembership?.role && userMembership.role !== "creator";

  const tabs = [
    { id: "threads", label: "Discussions", icon: FiMessageCircle },
    { id: "chat", label: "Chat", icon: FiMessageSquare },
    { id: "members", label: "Members", icon: FiUsers },
    { id: "rooms", label: "Rooms", icon: FiBookOpen },
    ...(isModeratorOrCreator()
      ? [
          { id: "moderation", label: "Moderation", icon: FiShield },
          { id: "settings", label: "Settings", icon: FiSettings },
        ]
      : []),
  ];

  // Loading State
  if (loading) {
    return (
      <div className="cdash">
        <div className="cdash-loading">
          <div className="cdash-loading-spinner" />
          <p>Loading community...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !community) {
    return (
      <div className="cdash">
        <div className="cdash-error">
          <div className="cdash-error-icon">
            <FiAlertCircle size={28} />
          </div>
          <h2>Community Not Found</h2>
          <p>{error || "The requested community could not be found."}</p>
          <button
            onClick={() => navigate("/communities")}
            className="cdash-error-btn"
          >
            <FiArrowLeft size={16} />
            Back to Communities
          </button>
        </div>
      </div>
    );
  }

  const communityImageUrl = community.signedPhotoURL || community.photoURL;

  return (
    <motion.div
      className={`cdash ${activeTab === "chat" ? "cdash-chat-active" : ""}`}
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Minimal Header - Single Row */}
      <header className="cdash-header">
        <div className="cdash-header-inner">
          {/* Left: Avatar + Title + Badges */}
          <div className="cdash-header-left">
            <div className="cdash-avatar">
              {communityImageUrl ? (
                <img src={communityImageUrl} alt={community.name} />
              ) : (
                <span className="cdash-avatar-initial">
                  {community.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>

            <div className="cdash-header-info">
              <h1 className="cdash-title">{community.name}</h1>

              <div className="cdash-badges">
                <span
                  className={`cdash-badge cdash-badge-type ${community.type}`}
                >
                  {community.type === "public" ? (
                    <FiGlobe size={10} />
                  ) : (
                    <FiLock size={10} />
                  )}
                  {community.type}
                </span>
                {getUserRoleBadge()}
              </div>

              {community.field && (
                <span className="cdash-field-tag">{community.field}</span>
              )}
            </div>
          </div>

          {/* Right: Action Buttons */}
          <div className="cdash-header-actions">
            {canLeave && (
              <button
                className="cdash-action-btn cdash-action-btn--danger"
                onClick={() => setShowLeaveModal(true)}
              >
                <FiLogOut size={14} />
                <span>Leave</span>
              </button>
            )}
            <button
              className="cdash-action-btn cdash-action-btn--secondary"
              onClick={() => navigate(`/communities/${communityId}/preview`)}
            >
              <FiEye size={14} />
              <span>Preview</span>
            </button>
            <button
              className="cdash-action-btn cdash-action-btn--ghost"
              onClick={() => navigate(-1)}
            >
              <FiArrowLeft size={14} />
              <span>Back</span>
            </button>
          </div>
        </div>
      </header>

      {/* Read-Only Banner */}
      <AnimatePresence>
        {isReadOnly && readOnlyMessage && (
          <motion.div
            className="cdash-readonly-banner"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="cdash-readonly-inner">
              <div className="cdash-readonly-info">
                <FiInfo size={14} />
                <span>{readOnlyMessage}</span>
              </div>
              <button
                className="cdash-readonly-join"
                onClick={handleJoinCommunity}
              >
                <FiUserPlus size={14} />
                {community
                  ? getJoinActionText(community, userMembership)
                  : "Join"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation Tabs */}
      <nav className="cdash-nav">
        <div className="cdash-nav-inner">
          <div className="cdash-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`cdash-tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <motion.main
        className="cdash-content"
        variants={contentVariants}
        initial="initial"
        animate="animate"
        key={activeTab}
      >
        {activeTab === "threads" && (
          <div className="cdash-discussions-layout">
            <CommunityThreads
              communityId={communityId}
              community={community}
              isModeratorOrCreator={isModeratorOrCreator()}
              userMembership={userMembership}
              isReadOnly={isReadOnly}
            />
            <aside className="cdash-discussions-sidebar">
              <CommunitySpecificSidebar communityId={communityId} />
            </aside>
          </div>
        )}

        {activeTab === "chat" && (
          <CommunityChat
            communityId={communityId}
            community={community}
            userRole={userMembership?.role}
            userMembership={userMembership}
            isReadOnly={isReadOnly}
          />
        )}

        {activeTab === "members" && (
          <CommunityMembers
            communityId={communityId}
            preview={false}
            previewData={dashboardData}
            community={community}
            userMembership={userMembership}
            isReadOnly={isReadOnly}
          />
        )}

        {activeTab === "rooms" && (
          <CommunityRooms
            communityId={communityId}
            rooms={dashboardData?.rooms}
            community={dashboardData?.community || community}
            userRole={
              dashboardData?.userMembership?.role ||
              community?.userMembership?.role
            }
            currentUser={
              dashboardData?.userMembership?.user ||
              community?.userMembership?.user
            }
          />
        )}

        {activeTab === "moderation" && isModeratorOrCreator() && (
          <ModerationDashboard
            communityId={communityId}
            userRole={userMembership?.role}
          />
        )}

        {activeTab === "settings" && isModeratorOrCreator() && (
          <CommunitySettings
            communityId={communityId}
            community={community}
            onCommunityUpdate={setCommunity}
          />
        )}
      </motion.main>

      {/* Mobile Sidebar FAB - Only visible on mobile/tablet when on threads tab */}
      {activeTab === "threads" && (
        <motion.button
          className="cdash-mobile-fab"
          onClick={() => setShowMobileSidebar(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Open community info"
        >
          <FiInfo size={22} />
        </motion.button>
      )}

      {/* Mobile Sidebar Drawer */}
      <AnimatePresence>
        {showMobileSidebar && (
          <>
            {/* Backdrop */}
            <motion.div
              className="cdash-drawer-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMobileSidebar(false)}
            />

            {/* Drawer */}
            <motion.aside
              className="cdash-drawer"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="cdash-drawer-header">
                <h3 className="cdash-drawer-title">Community Info</h3>
                <motion.button
                  className="cdash-drawer-close"
                  onClick={() => setShowMobileSidebar(false)}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiX size={20} />
                </motion.button>
              </div>
              <div className="cdash-drawer-content">
                <CommunitySpecificSidebar communityId={communityId} />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Leave Community Confirmation Modal */}
      <AnimatePresence>
        {showLeaveModal && (
          <>
            <motion.div
              className="cdash-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowLeaveModal(false)}
            />
            <motion.div
              className="cdash-modal"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
            >
              <div className="cdash-modal-header">
                <div className="cdash-modal-icon cdash-modal-icon--danger">
                  <FiLogOut size={24} />
                </div>
                <h3>Leave Community</h3>
                <button
                  className="cdash-modal-close"
                  onClick={() => setShowLeaveModal(false)}
                >
                  <FiX size={18} />
                </button>
              </div>
              <div className="cdash-modal-body">
                <p>
                  Are you sure you want to leave <strong>{community?.name}</strong>?
                </p>
                <p className="cdash-modal-warning">
                  You will lose access to all community content and will need to rejoin to participate again.
                </p>
              </div>
              <div className="cdash-modal-footer">
                <button
                  className="cdash-modal-btn cdash-modal-btn--secondary"
                  onClick={() => setShowLeaveModal(false)}
                  disabled={leaveLoading}
                >
                  Cancel
                </button>
                <button
                  className="cdash-modal-btn cdash-modal-btn--danger"
                  onClick={handleLeaveCommunity}
                  disabled={leaveLoading}
                >
                  {leaveLoading ? "Leaving..." : "Leave Community"}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CommunityDashboardNew;
